import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import api from '../services/api';
import { kontrolaFakturService } from '../services/kontrolaFakturService';

import {
  UploadStatusBar,
  CennikImportModal,
  FakturaXmlModal,
  MultiCennikImportModal,
  MultiCennikProgress,
  usePurchaseImports,
  WizardStepper,
  WizardDropZone,
  WrongFileNotice,
  CoverageWarning,
  AdminFallbackPanel,
  WizardSummary
} from '../components/purchase';

const KLUCZ_SESJI = 'v3sys_kreator_faktury';
const LIMIT_SESJI = 1024 * 1024; // 1 MB

/**
 * Kreator dodawania faktury zakupowej.
 *
 * KROK 1 cennik -> KROK 2 faktura XML -> KROK 3 gotowe.
 * Kreator NIE ma wlasnej logiki importu: uzywa hooka usePurchaseImports,
 * tych samych endpointow (import-cennik, save-cennik, preview-xml, save-xml)
 * i tych samych modali podgladu co strona faktur zakupowych.
 */
/** Krok odtworzony z sessionStorage (F5) - czytany bez czekania na efekt odtwarzania.
 *  Zwraca tez `sfd` zapisane w sesji: sesja z INNEGO dokumentu (albo z kreatora
 *  recznego) nie moze przejac kreatora otwartego z ?sfd=<id>. */
export const krokZSesji = (sfdId = null) => {
  try {
    const surowe = sessionStorage.getItem(KLUCZ_SESJI);
    const zapisane = surowe ? JSON.parse(surowe) : null;
    if (!zapisane || !sesjaPasuje(zapisane, sfdId)) return { krok: 1, sfd: sfdId };
    const krok = Number(zapisane.krok);
    return { krok: Number.isFinite(krok) && krok > 0 ? krok : 1, sfd: sfdId };
  } catch (e) {
    return { krok: 1, sfd: sfdId };
  }
};

/** Sesja kreatora nalezy do konkretnego dokumentu SFD (albo do kreatora recznego, sfd=null).
 *  Bez tego: po dodaniu jednej faktury z SFD sesja pamietala 'krok 2', a kolejny
 *  dokument (?sfd=<inny id>) otwieral sie od razu w kroku 2 BEZ wgrania pliku -
 *  pusty podglad, nic sie nie dodawalo (zgloszenie wlasciciela 2026-09-12, paragony). */
export const sesjaPasuje = (zapisane, sfdId) => String(zapisane?.sfd ?? '') === String(sfdId ?? '');

const PurchaseInvoiceWizardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ANEKS 3: ?sfd=<id> - faktura z kontroli SFD (pliki cennik/dokument z portalu, sklep z adresu dostawy)
  const sfdId = searchParams.get('sfd');
  const [sfd, setSfd] = useState({ info: null, blad: null, ladowanie: false, pliki: null });
  const { selectedLocation, locationId } = useLocation();
  // Sklep z adresu dostawy SFD ma pierwszenstwo nad wybranym w pasku - faktura
  // ma trafic tam, dokad SFD wyslalo towar, nie tam, gdzie kasjer jest zalogowany.
  const currentLocationId = sfd.info?.sklep_id
    || locationId || selectedLocation?.id || selectedLocation?.location_id;

  const { user } = useAuth();
  const isAdmin = user?.user_type === 'admin';
  const { hasPermission } = usePermissions();
  // Lokalizacja przekazana JAWNIE - domyslna z PermissionsContext to pierwsza
  // lokalizacja uzytkownika, nie ta wybrana na ekranie.
  const moznaOdblokowac = isAdmin || hasPermission('faktury_zakupu', 'import_bez_cennika', currentLocationId);

  // autoNavigate:false - bez tego hook po 1.5s wyrzucilby nas z kroku 2
  const imports = usePurchaseImports(currentLocationId, { autoNavigate: false });

  const [krok, setKrok] = useState(1);
  const [krok1Wynik, setKrok1Wynik] = useState(null);
  const [krok2Wynik, setKrok2Wynik] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [pominietoKrok1, setPominietoKrok1] = useState(false);
  const [zlyPlik, setZlyPlik] = useState(null);
  const [pustyCennik, setPustyCennik] = useState(false);
  const [pokrycieZBledu, setPokrycieZBledu] = useState(null);
  const [duplikat, setDuplikat] = useState(null);
  const [zapisAdmina, setZapisAdmina] = useState(false);
  // Identyfikatory (EAN/kod) z cennika wgranego w kroku 1. Sluza do sprawdzenia,
  // czy pozycje faktury w ogole wystepuja w TYM cenniku - wgranie cennika innego
  // dostawcy daje pokrycie bliskie zeru i od razu to widac.
  const [idCennika, setIdCennika] = useState(null);

  // Obiekty File wgranych plikow - pozwalaja przerzucic plik miedzy krokami
  // i PRZELICZYC podglad faktury po wgraniu cennika, bez proszenia uzytkownika
  // o ponowne szukanie po dysku. Osobne refy: jeden wspolny gubil plik faktury,
  // gdy uzytkownik wracal do kroku 1 po cennik.
  const plikCennikaRef = useRef(null);
  const plikFakturyRef = useRef(null);
  const multiInputRef = useRef(null);
  const odtworzonoRef = useRef(false);
  const sfdStartRef = useRef(null);   // id, dla ktorego auto-krok 1 juz poszedl (StrictMode / re-render)
  // Krok z sesji czytany PRZY PIERWSZYM RENDERZE - efekt zapisu sesji nizej nadpisuje
  // sessionStorage biezacym stanem (krok 1) zanim setKrok z odtwarzania sie wykona.
  const krokStartowyRef = useRef(krokZSesji(searchParams.get('sfd')).krok);
  const [blad, setBlad] = useState(null);

  const { setCennikOptions, setFakturaPreview, setShowFakturaModal } = imports;

  // --- opcje cennika: kasjer nie moze zapisywac z opcjami, ktorych nie widzial ---
  useEffect(() => {
    setCennikOptions({ update_names: true, update_prices: true, create_new: true });
  }, [setCennikOptions]);

  // --- odtworzenie stanu po odswiezeniu strony (F5) ---
  useEffect(() => {
    if (odtworzonoRef.current) return;
    odtworzonoRef.current = true;
    try {
      const surowe = sessionStorage.getItem(KLUCZ_SESJI);
      if (!surowe) return;
      const zapisane = JSON.parse(surowe);
      if (!sesjaPasuje(zapisane, searchParams.get('sfd'))) {
        // sesja z innego dokumentu / innego trybu - zaczynamy od zera
        sessionStorage.removeItem(KLUCZ_SESJI);
        return;
      }
      if (zapisane.krok) setKrok(zapisane.krok);
      if (zapisane.krok1Wynik) setKrok1Wynik(zapisane.krok1Wynik);
      if (zapisane.krok2Wynik) setKrok2Wynik(zapisane.krok2Wynik);
      if (zapisane.invoiceId) setInvoiceId(zapisane.invoiceId);
      if (zapisane.pominietoKrok1) setPominietoKrok1(true);
      if (zapisane.fakturaPreview) {
        setFakturaPreview(zapisane.fakturaPreview);
        setShowFakturaModal(false);
      }
    } catch (e) {
      console.warn('[Kreator] Nie udalo sie odtworzyc stanu z sessionStorage', e);
    }
  }, [setFakturaPreview, setShowFakturaModal, searchParams]);

  // --- zapis stanu do sessionStorage ---
  useEffect(() => {
    if (!odtworzonoRef.current) return;
    try {
      const dane = JSON.stringify({
        sfd: searchParams.get('sfd'),
        krok, krok1Wynik, krok2Wynik, invoiceId, pominietoKrok1,
        fakturaPreview: imports.fakturaPreview
      });
      if (dane.length > LIMIT_SESJI) return; // duza faktura - nie zasmiecamy sesji
      sessionStorage.setItem(KLUCZ_SESJI, dane);
    } catch (e) {
      // brak miejsca w sessionStorage nie moze wywalic kreatora
    }
  }, [krok, krok1Wynik, krok2Wynik, invoiceId, pominietoKrok1, imports.fakturaPreview, searchParams]);

  const wyczyscSesje = useCallback(() => {
    try { sessionStorage.removeItem(KLUCZ_SESJI); } catch (e) { /* ignoruj */ }
  }, []);

  // --- Czy faktura pasuje do cennika wgranego w kroku 1? ---
  // Porownujemy POZYCJE, nie dostawcow: jesli kasjer wgral cennik innej hurtowni,
  // prawie zadna pozycja faktury nie znajdzie sie w tym cenniku.
  // Liczymy tylko dla pozycji, ktore maja jakikolwiek identyfikator - pozycja bez
  // EAN i bez kodu (usluga, transport) nie moze byc pokryta zadnym cennikiem.
  const zgodnoscZCennikiem = useMemo(() => {
    if (!idCennika || idCennika.size === 0) return null;
    const pozycje = imports.fakturaPreview?.items;
    if (!Array.isArray(pozycje) || pozycje.length === 0) return null;

    let zIdentyfikatorem = 0;
    let wCenniku = 0;
    pozycje.forEach(poz => {
      const ean = String(poz.ean || '').trim().toLowerCase();
      const kod = String(poz.kod_produktu || '').trim().toLowerCase();
      if (!ean && !kod) return;
      zIdentyfikatorem += 1;
      if ((ean && idCennika.has(ean)) || (kod && idCennika.has(kod))) wCenniku += 1;
    });

    if (zIdentyfikatorem === 0) return null;
    const procent = Math.round((wCenniku / zIdentyfikatorem) * 100);
    return { wCenniku, zIdentyfikatorem, procent, pasuje: procent >= 50 };
  }, [idCennika, imports.fakturaPreview]);

  // --- pusty cennik: 0 pozycji = to nie jest cennik ---
  useEffect(() => {
    const preview = imports.cennikPreview;
    if (!preview) return;
    const total = preview.stats?.total != null ? preview.stats.total : (preview.products?.length || 0);
    if (total === 0) {
      setPustyCennik(true);
      imports.closeCennikModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imports.cennikPreview]);

  // ===================== KROK 1 =====================

  const wgrajCennik = async (plik, opcje = {}) => {
    plikCennikaRef.current = plik;
    setZlyPlik(null);
    setPustyCennik(false);
    setBlad(null);
    const wynik = await imports.handleFileUpload('cennik', plik, opcje);
    if (!wynik.ok && wynik.errorCode === 'ZLY_TYP_PLIKU') {
      // WrongFileNotice pokazuje ten sam komunikat, tylko bogatszy (przyciski,
      // diagnostyka) - pasek statusu z hooka trzeba zgasic, zeby nie dublowal tresci.
      imports.clearUploadStatus();
      setZlyPlik({
        file: plik,
        wykryto: wynik.wykryto,
        oczekiwano: wynik.oczekiwano || 'cennik',
        komunikat: wynik.komunikat,
        diagnostyka: wynik.diagnostyka,
        moznaWymusic: wynik.moznaWymusic,
        sugerowanyKrok: wynik.sugerowanyKrok || 2
      });
    } else if (!wynik.ok) {
      setBlad(wynik.komunikat || 'Nie udalo sie wczytac cennika');
    }
  };

  // Po zapisie cennika przeliczamy podglad faktury OD NOWA - inaczej krok 2
  // pokazywal stare (czerwone) pokrycie sprzed wgrania cennika i kazal
  // uzytkownikowi zrobic to, co wlasnie zrobil.
  const odswiezPodgladFaktury = useCallback(async () => {
    if (!plikFakturyRef.current) return;
    setPokrycieZBledu(null);
    await imports.handleFileUpload('xml', plikFakturyRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imports.handleFileUpload]);

  // Zbiera EAN-y i kody z podgladu cennika. Musi byc wolane PRZED zapisem,
  // bo zapis zamyka podglad i czysci liste produktow.
  const zbierzIdentyfikatory = (produkty) => {
    const zbior = new Set();
    (produkty || []).forEach(p => {
      const ean = String(p.ean || '').trim();
      const kod = String(p.kod || '').trim();
      if (ean) zbior.add(ean.toLowerCase());
      if (kod) zbior.add(kod.toLowerCase());
    });
    return zbior;
  };

  const zapiszCennik = async () => {
    const identyfikatory = zbierzIdentyfikatory(imports.cennikPreview?.products);
    const wynik = await imports.saveCennikImport();
    if (wynik?.ok) {
      setIdCennika(identyfikatory);
      setKrok1Wynik(wynik.stats);
      setPominietoKrok1(false);
      setKrok(2);
      await odswiezPodgladFaktury();
    }
  };

  const zapiszWieleCennikow = async () => {
    const identyfikatory = zbierzIdentyfikatory(imports.multiCennikPreview?.products);
    const wynik = await imports.saveMultiCennikImport();
    if (wynik?.ok) {
      setIdCennika(identyfikatory);
      setKrok1Wynik(wynik.stats);
      setPominietoKrok1(false);
      setKrok(2);
      await odswiezPodgladFaktury();
    }
  };

  // ===================== SFD: ?sfd=<id> =====================
  // Pobierz dane faktury z kontroli, oba XML-e z zapisanego ZIP-a i uruchom
  // krok 1 z cennikiem. Dokument laduje do plikFakturyRef - po zapisie cennika
  // istniejacy odswiezPodgladFaktury wgra go w kroku 2. Zapisy tylko na klik.
  useEffect(() => {
    if (!sfdId || sfdStartRef.current === sfdId) return undefined;
    sfdStartRef.current = sfdId;
    let aktywny = true;
    const krokPoF5 = krokStartowyRef.current;
    setSfd({ info: null, blad: null, ladowanie: true, pliki: null });
    (async () => {
      let info;
      try {
        info = await kontrolaFakturService.dodajInfo(sfdId);
      } catch (err) {
        if (!aktywny) return;
        const komunikat = (err?.status === 403 || err?.isPermissionError)
          ? 'Dodawanie faktury z SFD jest dostępne tylko dla administratora (brak uprawnień do danych kontroli faktur). Wgraj pliki ręcznie albo poproś administratora.'
          : `Nie udało się pobrać danych faktury z kontroli SFD: ${err?.message || 'błąd'}`;
        setSfd({ info: null, blad: komunikat, ladowanie: false, pliki: null });
        return;
      }
      if (!aktywny) return;
      setSfd({ info, blad: null, ladowanie: true, pliki: null });
      if (info?.plik?.status && info.plik.status !== 'pobrany') {
        setSfd({ info, blad: `Plik ZIP tej faktury nie jest jeszcze pobrany z portalu (status: ${info.plik.status}). Wróć do kontroli faktur i kliknij „Pobierz plik”.`, ladowanie: false, pliki: null });
        return;
      }
      // ANEKS 4: paczka z maila bywa bez cennika - wtedy jest tylko dokument i krok 1 odpada
      const bezCennika = info?.plik?.bez_cennika === true;
      try {
        const [cennik, dokument] = await Promise.all([
          bezCennika ? Promise.resolve(null) : kontrolaFakturService.pobierzXml(sfdId, 'cennik', info?.numer),
          kontrolaFakturService.pobierzXml(sfdId, 'dokument', info?.numer),
        ]);
        if (!aktywny) return;
        plikFakturyRef.current = dokument;
        setSfd({ info, blad: null, ladowanie: false, pliki: { cennik: cennik?.name || null, dokument: dokument.name, bezCennika } });
        // Po F5 w kroku 2/3 nie zaczynamy od nowa - podglad odtwarza sessionStorage.
        if (krokPoF5 <= 1) {
          if (bezCennika) {
            // Krok 1 pominiety swiadomie (nie ma czego wgrywac) - od razu podglad faktury.
            setPominietoKrok1(true);
            setKrok(2);
            await wgrajFakture(dokument);
          } else {
            await wgrajCennik(cennik);
          }
        }
      } catch (err) {
        if (!aktywny) return;
        const komunikat = (err?.status === 403 || err?.isPermissionError)
          ? 'Pliki z SFD są dostępne tylko dla administratora.'
          : `Nie udało się pobrać plików XML z SFD: ${err?.message || 'błąd'}`;
        setSfd({ info, blad: komunikat, ladowanie: false, pliki: null });
      }
    })();
    return () => { aktywny = false; };
    // wgrajCennik zmienia sie co render (zwykla funkcja) - efekt ma ruszac tylko na zmiane ?sfd
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sfdId]);

  const wrocDoKontroli = () => { wyczyscSesje(); navigate('/admin?tab=kontrola-faktur'); };

  // ===================== KROK 2 =====================

  const wgrajFakture = async (plik, opcje = {}) => {
    plikFakturyRef.current = plik;
    setZlyPlik(null);
    setPokrycieZBledu(null);
    setDuplikat(null);
    setBlad(null);
    const wynik = await imports.handleFileUpload('xml', plik, opcje);
    if (!wynik.ok && wynik.errorCode === 'ZLY_TYP_PLIKU') {
      // patrz komentarz w wgrajCennik - unikamy zdublowanego komunikatu
      imports.clearUploadStatus();
      setZlyPlik({
        file: plik,
        wykryto: wynik.wykryto,
        oczekiwano: wynik.oczekiwano || 'faktura',
        komunikat: wynik.komunikat,
        diagnostyka: wynik.diagnostyka,
        moznaWymusic: wynik.moznaWymusic,
        sugerowanyKrok: wynik.sugerowanyKrok || 1
      });
    } else if (!wynik.ok) {
      setBlad(wynik.komunikat || 'Nie udalo sie wczytac faktury');
    }
  };

  const zapiszFakture = async (opcje = {}) => {
    const podglad = imports.fakturaPreview;
    setBlad(null);
    const wynik = await imports.saveFakturaImport(opcje);

    if (wynik?.ok) {
      setInvoiceId(wynik.invoiceId);
      setKrok2Wynik({
        numer: podglad?.invoice?.numer_faktury || '-',
        pozycje: podglad?.stats?.total_items || 0,
        zmapowane: podglad?.stats?.mapped || 0,
        brutto: podglad?.invoice?.suma_brutto || 0,
        wymagaUzupelnienia: 0
      });
      setKrok(3);
      return;
    }

    if (wynik?.errorCode === 'CENNIK_WYMAGANY') {
      setPokrycieZBledu({
        ok: false,
        total: podglad?.stats?.total_items || 0,
        brak: wynik.brakujace?.length || 0,
        brakujace: wynik.brakujace || []
      });
      setBlad(wynik.komunikat || null);
    } else if (wynik?.errorCode === 'FAKTURA_ISTNIEJE' || wynik?.errorCode === 'DUPLIKAT_FAKTURY') {
      // 409 FAKTURA_ISTNIEJE: numer jest juz w systemie (w dowolnym sklepie). Bez
      // obejscia - wlasciciel: bez swiadomego potwierdzenia admina faktura nie
      // wchodzi drugi raz, a na razie nie ma zadnego "zapisz mimo to".
      const ist = wynik.istniejaca || null;
      setDuplikat({
        komunikat: wynik.komunikat || `Faktura ${podglad?.invoice?.numer_faktury || ''} jest już w systemie. Nie dodano drugi raz.`,
        invoiceId: ist?.id || wynik.istniejacaFakturaId || wynik.invoiceId || null,
        sklep: ist?.sklep || null,
        numer: ist?.numer || podglad?.invoice?.numer_faktury || null
      });
      setBlad(wynik.komunikat || 'Faktura o tym numerze jest już w systemie');
      // Modal to overlay - baner z odnosnikiem do istniejacej faktury jest pod nim.
      // Zamykamy podglad, zeby wyjscia byly klikalne; podglad wraca przyciskiem
      // "Pokaz podglad wczytanej faktury".
      setShowFakturaModal(false);
    } else if (wynik && !wynik.ok) {
      setBlad(wynik.komunikat || 'Nie udalo sie zapisac faktury');
    }
  };

  // Pokrycie: z podgladu (preview-xml) albo z odrzuconego zapisu
  const pokrycie = pokrycieZBledu || imports.fakturaPreview?.cennik_coverage || null;
  const brakPokrycia = !!pokrycie && pokrycie.ok === false && pokrycie.zwolniony !== true;

  const wrocDoKroku1 = () => {
    setKrok(1);
    setPokrycieZBledu(null);
    setBlad(null);
    setShowFakturaModal(false);
  };

  // --- furtka administratora: wariant A (uzupelnienie danych) ---
  const zapiszUzupelnioneProdukty = async (wiersze) => {
    if (zapisAdmina) return;
    setZapisAdmina(true);
    try {
      const res = await api.post('/purchase-invoices/kreator/uzupelnij-produkty', {
        pozycje: wiersze,
        location_id: currentLocationId
      });
      if (res.data?.success) {
        toast.success(res.data.message || 'Produkty utworzone');
        setPokrycieZBledu(null);
        // Ponawiamy podglad TEGO SAMEGO pliku, zeby pokrycie policzylo sie od nowa
        await odswiezPodgladFaktury();
      } else {
        toast.error(res.data?.message || res.data?.error || 'Nie udalo sie utworzyc produktow');
      }
    } catch (e) {
      toast.error(e?.message || 'Blad tworzenia produktow');
    } finally {
      setZapisAdmina(false);
    }
  };

  // --- furtka administratora: wariant B (zapis mimo brakow) ---
  const zapiszMimoBrakow = async (powod) => {
    if (zapisAdmina) return;
    const podglad = imports.fakturaPreview;
    if (!podglad?.invoice) return;
    setZapisAdmina(true);
    try {
      const res = await api.post('/purchase-invoices/save-xml-bez-cennika', {
        invoice: podglad.invoice,
        items: podglad.items,
        filename: podglad.filename,
        location_id: currentLocationId,
        powod
      });
      if (res.data?.success) {
        setInvoiceId(res.data.data?.invoice_id || null);
        setKrok2Wynik({
          numer: podglad.invoice?.numer_faktury || '-',
          pozycje: podglad.stats?.total_items || 0,
          zmapowane: podglad.stats?.mapped || 0,
          brutto: podglad.invoice?.suma_brutto || 0,
          wymagaUzupelnienia: pokrycie?.brakujace?.length || 0
        });
        setPokrycieZBledu(null);
        imports.closeFakturaModal();
        setKrok(3);
        toast.success('Faktura zapisana z pominieciem cennika');
      } else {
        toast.error(res.data?.message || res.data?.error || 'Nie udalo sie zapisac faktury');
      }
    } catch (e) {
      toast.error(e?.message || 'Blad zapisu faktury');
    } finally {
      setZapisAdmina(false);
    }
  };

  // ===================== KROK 3 =====================

  const zacznijOdNowa = () => {
    wyczyscSesje();
    setKrok(1);
    setKrok1Wynik(null);
    setKrok2Wynik(null);
    setInvoiceId(null);
    setPominietoKrok1(false);
    setZlyPlik(null);
    setPokrycieZBledu(null);
    setDuplikat(null);
    setBlad(null);
    plikCennikaRef.current = null;
    plikFakturyRef.current = null;
  };

  const kolejnaFaktura = () => {
    setKrok2Wynik(null);
    setInvoiceId(null);
    setPokrycieZBledu(null);
    setDuplikat(null);
    setKrok(2);
  };

  // ===================== RENDER =====================

  const stanUploadu = (typ) => {
    const us = imports.uploadStatus;
    if (!us || typeof us !== 'object' || us.type !== typ) return 'pusty';
    if (us.status === 'uploading') return 'wgrywanie';
    if (us.status === 'error') return 'blad';
    return 'ok';
  };

  const linkStyle = {
    padding: '4px 10px', fontSize: '11px', borderRadius: '4px',
    border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)',
    color: 'var(--text-secondary)', cursor: 'pointer'
  };

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Naglowek */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Kreator dodawania faktury</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Najpierw cennik (nazwy i stawki VAT), potem faktura (ceny zakupu i ilo{'ś'}ci).
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {sfdId && (
              <button style={linkStyle} onClick={wrocDoKontroli}>Wr{'ó'}{'ć'} do kontroli faktur</button>
            )}
            <button style={linkStyle} onClick={() => navigate('/purchase-invoices')}>
              Wr{'ó'}{'ć'} do faktur zakupowych
            </button>
          </div>
        </div>

        {/* ANEKS 3: baner faktury z kontroli SFD */}
        {sfdId && (
          <div data-testid="baner-sfd" style={{
            marginTop: '10px', padding: '8px 10px', borderRadius: '4px', fontSize: '12px',
            backgroundColor: sfd.blad ? 'var(--danger-light)' : 'var(--info-light)',
            border: `1px solid ${sfd.blad ? 'var(--danger)' : 'var(--info)'}`,
            color: sfd.blad ? 'var(--danger)' : 'var(--text)',
          }}>
            {sfd.blad ? (
              <span>{'⛔'} {sfd.blad}</span>
            ) : sfd.info ? (
              <div>
                <div>
                  <strong>{sfd.info.typ === 'paragon' ? 'Paragon z SFD Renoma' : 'Faktura z SFD'}: {sfd.info.numer}</strong>
                  {sfd.info.brutto !== undefined && sfd.info.brutto !== null && <> · {Number(sfd.info.brutto).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł</>}
                  {' · '}
                  <span data-testid="sfd-sklep">
                    Sklep: <strong>{sfd.info.sklep_nazwa || (sfd.info.sklep_id ? `#${sfd.info.sklep_id}` : 'nieznany')}</strong>
                    {sfd.info.sklep_id ? ' (z adresu dostawy SFD)' : ''}
                  </span>
                  {sfd.info.adres_dostawy && <> · {sfd.info.adres_dostawy}</>}
                  {sfd.info.powiazane_wz && <> · WZ: {sfd.info.powiazane_wz}</>}
                  {sfd.info.plik?.zrodlo && (
                    <> · <span data-testid="sfd-zrodlo-pliku">plik {sfd.info.plik.zrodlo === 'mail' ? 'z maila' : 'z portalu'}
                      {sfd.info.plik.zrodlo === 'mail' && sfd.info.plik.mail_temat ? ` („${sfd.info.plik.mail_temat}”)` : ''}</span></>
                  )}
                </div>
                {!sfd.info.sklep_id && (
                  <div style={{ color: 'var(--warning)', marginTop: '4px' }}>
                    {'⚠️'} SFD nie ma przypisanego sklepu dla tego adresu {'—'} faktura trafi do sklepu wybranego w pasku.
                  </div>
                )}
                {Array.isArray(sfd.info.w_systemie) && sfd.info.w_systemie.length > 0 && (
                  <div data-testid="sfd-ostrzezenie-wz" style={{ color: 'var(--warning)', marginTop: '4px', fontWeight: 600 }}>
                    {'⚠️'} Towar ju{'ż'} przyj{'ę'}ty jako {sfd.info.w_systemie.map(w => w.przez_wz).filter(Boolean).join(', ') || sfd.info.powiazane_wz || 'WZ'}
                    {' '}({sfd.info.w_systemie.map(w => `${w.sklep || '?'} · ${w.dodal || '?'}`).join('; ')}). Zapis tej faktury doda towar drugi raz {'—'} sprawd{'ź'} przed klikni{'ę'}ciem „Zapisz”.
                  </div>
                )}
                <div style={{ color: 'var(--text-muted)', marginTop: '4px', fontSize: '11px' }}>
                  {sfd.ladowanie
                    ? 'Pobieram pliki z SFD…'
                    : sfd.pliki
                      ? (sfd.pliki.bezCennika
                        ? `Paczka ${sfd.info?.plik?.zrodlo === 'mail' ? 'z maila' : 'z SFD'} zawiera sam dokument — krok 1 (cennik) pominięty, od razu krok 2: ${sfd.pliki.dokument}. Nic nie zapisuje się samo — „Zapisz” klikasz jak zwykle.`
                        : `Pliki z SFD: ${sfd.pliki.cennik} (krok 1) · ${sfd.pliki.dokument} (krok 2, wczyta się po zapisie cennika). Nic nie zapisuje się samo — „Zapisz” klikasz jak zwykle.`)
                      : ''}
                </div>
              </div>
            ) : (
              <span>Pobieram dane faktury z kontroli SFD…</span>
            )}
          </div>
        )}
      </div>

      <WizardStepper krok={krok} />

      <UploadStatusBar
        uploadStatus={imports.uploadStatus}
        uploadProgress={imports.uploadProgress}
        onClear={imports.clearUploadStatus}
      />

      {/* ===== KROK 1 ===== */}
      {krok === 1 && (
        <div className="card">
          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Krok 1 {'—'} cennik dostawcy</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            Cennik zawiera poprawne nazwy i stawki VAT. Bez niego faktura utworzy pozycje
            bez nazwy i ze z{'ł'}{'ą'} stawk{'ą'}.
          </div>

          {zlyPlik && (
            <WrongFileNotice
              wykryto={zlyPlik.wykryto}
              oczekiwano={zlyPlik.oczekiwano}
              filename={zlyPlik.file?.name}
              komunikat={zlyPlik.komunikat}
              diagnostyka={zlyPlik.diagnostyka}
              sugerowanyKrok={zlyPlik.sugerowanyKrok}
              moznaWymusic={zlyPlik.moznaWymusic}
              onWymus={() => {
                const plik = zlyPlik.file;
                setZlyPlik(null);
                if (plik) wgrajCennik(plik, { wymusTyp: true });
              }}
              onUzyjWInnymKroku={() => {
                const plik = zlyPlik.file;
                setZlyPlik(null);
                // NIE ustawiamy pominietoKrok1 - przelozenie pliku do kroku 2
                // nie jest swiadoma decyzja "cennik juz jest w systemie".
                setKrok(2);
                if (plik) wgrajFakture(plik);
              }}
              onWybierzInny={() => setZlyPlik(null)}
            />
          )}

          {blad && (
            <div style={{
              backgroundColor: 'var(--danger-light)', border: '1px solid var(--danger)',
              color: 'var(--danger)', borderRadius: '4px', padding: '8px 10px',
              fontSize: '12px', marginBottom: '12px'
            }}>
              {'⛔'} {blad}
            </div>
          )}

          {pustyCennik && (
            <div style={{
              backgroundColor: 'var(--danger-light)', border: '1px solid var(--danger)',
              color: 'var(--danger)', borderRadius: '4px', padding: '8px 10px',
              fontSize: '12px', fontWeight: '600', marginBottom: '12px'
            }}>
              {'⛔'} Ten plik nie zawiera {'ż'}adnej pozycji (brak element{'ó'}w TOWAR).
              To najprawdopodobniej nie jest cennik.
            </div>
          )}

          <WizardDropZone
            tytul="Przeciągnij cennik dostawcy (.xml)"
            opis="albo kliknij, żeby wybrać plik z dysku"
            accept=".xml"
            onPlik={wgrajCennik}
            stan={stanUploadu('cennik')}
            postep={imports.uploadProgress}
          />

          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            <button
              style={linkStyle}
              onClick={() => { setPominietoKrok1(true); setZlyPlik(null); setKrok(2); }}
            >
              Cennik tego dostawcy jest ju{'ż'} w systemie {'—'} pomi{'ń'} krok
            </button>
            <button style={linkStyle} onClick={() => multiInputRef.current && multiInputRef.current.click()}>
              Wgraj kilka cennik{'ó'}w naraz
            </button>
            <input
              ref={multiInputRef}
              type="file"
              accept=".xml"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const pliki = e.target.files;
                e.target.value = '';
                if (pliki && pliki.length) imports.handleMultipleCennikUpload(pliki);
              }}
            />
          </div>

          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '8px' }}>
            Pomini{'ę'}cie kroku nie omija kontroli {'—'} system i tak sprawdzi ka{'ż'}d{'ą'} pozycj{'ę'} faktury.
          </div>
        </div>
      )}

      {/* ===== KROK 2 ===== */}
      {krok === 2 && (
        <div className="card">
          {(krok1Wynik || pominietoKrok1) && (
            <div style={{
              backgroundColor: krok1Wynik ? 'var(--success-light)' : 'var(--info-light)',
              border: `1px solid ${krok1Wynik ? 'var(--success)' : 'var(--info)'}`,
              borderRadius: '4px', padding: '6px 10px', fontSize: '12px',
              color: krok1Wynik ? 'var(--success)' : 'var(--info)',
              marginBottom: '12px', display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', gap: '8px', flexWrap: 'wrap'
            }}>
              <span>
                {krok1Wynik
                  ? `✅ Cennik ${krok1Wynik.filename || ''}: utworzono ${krok1Wynik.created || 0}, zaktualizowano ${krok1Wynik.updated || 0}, odrzucono ${krok1Wynik.skipped || 0}`
                  : 'ℹ️ Krok 1 pominięty — cennik miał już być w systemie'}
              </span>
              <button style={linkStyle} onClick={() => setKrok(1)}>Wgraj kolejny cennik</button>
            </div>
          )}

          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Krok 2 {'—'} faktura XML</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            Faktura wnosi ceny zakupu i ilo{'ś'}ci. Nazwy i stawki VAT bierzemy z cennika.
          </div>

          {/* Czy to na pewno cennik OD TEGO dostawcy - porownanie pozycji */}
          {zgodnoscZCennikiem && (
            <div style={{
              backgroundColor: zgodnoscZCennikiem.pasuje ? 'var(--success-light)' : 'var(--warning-light)',
              border: `1px solid ${zgodnoscZCennikiem.pasuje ? 'var(--success)' : 'var(--warning)'}`,
              color: zgodnoscZCennikiem.pasuje ? 'var(--success)' : 'var(--warning)',
              borderRadius: '4px', padding: '8px 10px', fontSize: '12px', marginBottom: '12px'
            }}>
              {zgodnoscZCennikiem.pasuje ? (
                <span>
                  {'✅'} Faktura pasuje do wgranego cennika{': '}
                  <strong>{zgodnoscZCennikiem.wCenniku} z {zgodnoscZCennikiem.zIdentyfikatorem}</strong>
                  {' '}pozycji wyst{'ę'}puje w cenniku.
                </span>
              ) : (
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                    {'⚠️'} Ta faktura chyba nie pasuje do wgranego cennika{' — '}
                    tylko <strong>{zgodnoscZCennikiem.wCenniku} z {zgodnoscZCennikiem.zIdentyfikatorem}</strong>
                    {' '}pozycji ({zgodnoscZCennikiem.procent}%) wyst{'ę'}puje w cenniku.
                  </div>
                  <div style={{ color: 'var(--text-secondary)' }}>
                    Sprawd{'ź'}, czy cennik i faktura s{'ą'} od tego samego dostawcy.
                    Cennik innej hurtowni m{'ó'}g{'ł'} zmieni{'ć'} nazwy i ceny nie tym produktom.
                  </div>
                </div>
              )}
            </div>
          )}

          {zlyPlik && (
            <WrongFileNotice
              wykryto={zlyPlik.wykryto}
              oczekiwano={zlyPlik.oczekiwano}
              filename={zlyPlik.file?.name}
              komunikat={zlyPlik.komunikat}
              diagnostyka={zlyPlik.diagnostyka}
              sugerowanyKrok={zlyPlik.sugerowanyKrok}
              moznaWymusic={zlyPlik.moznaWymusic}
              onWymus={() => {
                const plik = zlyPlik.file;
                setZlyPlik(null);
                if (plik) wgrajFakture(plik, { wymusTyp: true });
              }}
              onUzyjWInnymKroku={() => {
                const plik = zlyPlik.file;
                setZlyPlik(null);
                setKrok(1);
                if (plik) wgrajCennik(plik);
              }}
              onWybierzInny={() => setZlyPlik(null)}
            />
          )}

          {blad && !duplikat && (
            <div style={{
              backgroundColor: 'var(--danger-light)', border: '1px solid var(--danger)',
              color: 'var(--danger)', borderRadius: '4px', padding: '8px 10px',
              fontSize: '12px', marginBottom: '12px'
            }}>
              {'⛔'} {blad}
            </div>
          )}

          {duplikat && (
            <div data-testid="baner-duplikatu" style={{
              backgroundColor: 'var(--danger-light)', border: '1px solid var(--danger)',
              color: 'var(--danger)', borderRadius: '4px', padding: '8px 10px', fontSize: '12px', marginBottom: '12px'
            }}>
              <div style={{ fontWeight: 600 }}>{'⛔'} Ta faktura jest ju{'ż'} w systemie {'—'} nie dodano drugi raz.</div>
              <div style={{ marginTop: '4px' }}>{duplikat.komunikat}</div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                {duplikat.invoiceId && (
                  <button
                    style={linkStyle}
                    onClick={() => { wyczyscSesje(); navigate(`/purchase-invoices/${duplikat.invoiceId}`); }}
                  >
                    Otw{'ó'}rz istniej{'ą'}c{'ą'} faktur{'ę'}{duplikat.sklep ? ` (${duplikat.sklep})` : ''}
                  </button>
                )}
                <button style={linkStyle} onClick={kolejnaFaktura}>
                  Wgraj inn{'ą'} faktur{'ę'}
                </button>
              </div>
              {/* Celowo BEZ przycisku "zapisz mimo to": numer faktury jest unikalny w calym
                  systemie (wszystkie sklepy). Obejscie tylko po decyzji wlasciciela. */}
            </div>
          )}

          <WizardDropZone
            tytul="Przeciągnij fakturę od dostawcy (.xml)"
            opis="albo kliknij, żeby wybrać plik z dysku"
            accept=".xml"
            onPlik={wgrajFakture}
            stan={stanUploadu('xml')}
            postep={imports.uploadProgress}
          />

          {imports.fakturaPreview && !imports.showFakturaModal && (
            <button
              style={{ ...linkStyle, marginTop: '10px' }}
              onClick={() => setShowFakturaModal(true)}
            >
              Poka{'ż'} podgl{'ą'}d wczytanej faktury ({imports.fakturaPreview.invoice?.numer_faktury || 'brak numeru'})
            </button>
          )}
        </div>
      )}

      {/* ===== KROK 3 ===== */}
      {krok === 3 && (
        <div className="card">
          <WizardSummary
            krok1Wynik={krok1Wynik}
            krok2Wynik={krok2Wynik}
            invoiceId={invoiceId}
            onOtworzFakture={() => { wyczyscSesje(); navigate(`/purchase-invoices/${invoiceId}`); }}
            onKolejnaFaktura={kolejnaFaktura}
            onOdNowa={zacznijOdNowa}
            onWrocDoListy={() => { wyczyscSesje(); navigate('/purchase-invoices/list'); }}
          />
          {sfdId && (
            <div style={{ marginTop: '10px' }}>
              <button data-testid="btn-wroc-do-kontroli" onClick={wrocDoKontroli}
                style={{ padding: '6px 12px', fontSize: '11px', borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff', fontWeight: 600 }}>
                Wr{'ó'}{'ć'} do kontroli faktur
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== MODALE (istniejace komponenty podgladu) ===== */}
      {imports.showCennikModal && (
        <CennikImportModal
          cennikPreview={imports.cennikPreview}
          cennikOptions={imports.cennikOptions}
          setCennikOptions={imports.setCennikOptions}
          cennikFilter={imports.cennikFilter}
          setCennikFilter={imports.setCennikFilter}
          cennikSearch={imports.cennikSearch}
          setCennikSearch={imports.setCennikSearch}
          isSavingCennik={imports.isSavingCennik}
          getFilteredCennikProducts={imports.getFilteredCennikProducts}
          onSave={zapiszCennik}
          onClose={imports.closeCennikModal}
        />
      )}

      <MultiCennikProgress progress={imports.multiCennikProgress} />

      {imports.showMultiCennikModal && (
        <MultiCennikImportModal
          multiCennikPreview={imports.multiCennikPreview}
          cennikOptions={imports.cennikOptions}
          setCennikOptions={imports.setCennikOptions}
          multiCennikFilter={imports.multiCennikFilter}
          setMultiCennikFilter={imports.setMultiCennikFilter}
          multiCennikSearch={imports.multiCennikSearch}
          setMultiCennikSearch={imports.setMultiCennikSearch}
          isSavingMultiCennik={imports.isSavingMultiCennik}
          getFilteredMultiCennikProducts={imports.getFilteredMultiCennikProducts}
          onSave={zapiszWieleCennikow}
          onClose={imports.closeMultiCennikModal}
        />
      )}

      {imports.showFakturaModal && (
        <FakturaXmlModal
          fakturaPreview={imports.fakturaPreview}
          isSavingFaktura={imports.isSavingFaktura}
          onSave={() => zapiszFakture()}
          onClose={imports.closeFakturaModal}
          blad={blad}
          coverage={pokrycie}
          onWrocDoKroku1={wrocDoKroku1}
          moznaOdblokowac={moznaOdblokowac}
          slotPodTabela={brakPokrycia ? (
            <CoverageWarning
              coverage={pokrycie}
              onWrocDoKroku1={wrocDoKroku1}
              moznaOdblokowac={moznaOdblokowac}
            >
              <AdminFallbackPanel
                // key wymusza przeladowanie formularza, gdy lista brakow sie zmieni
                // (np. po czesciowym uzupelnieniu) - bez tego zostawaly stare wiersze
                key={(pokrycie.brakujace || []).map(b => b.ean || b.kod_produktu || '?').join('|')}
                brakujace={pokrycie.brakujace || []}
                onZapiszProdukty={zapiszUzupelnioneProdukty}
                onZapiszMimoBrakow={zapiszMimoBrakow}
                isSaving={zapisAdmina}
              />
            </CoverageWarning>
          ) : null}
        />
      )}
    </div>
  );
};

export default PurchaseInvoiceWizardPage;
